<template>
  <div class="settings-block latest-sessions">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{ t('Latest Sessions') }}
      </div>
      <div class="settings-block__title-buttons">
        <ui-button
          :text="t('Close All Sessions')"
          icon="fas fa-times"
          button-style="outline-alt"
          @click="submit"
        />
      </div>
    </div>

    <p class="latest-sessions__intro">
      {{ t('These are the web browsers currently logged in to your account.') }}
    </p>

    <div class="settings-layout__table-wrapper">
      <ui-table
        class="latest-sessions__table"
        :headers="tableHeaders"
        :items="rows"
        :use-search="false"
        :loading="sessionsLoading"
        :show-pagination="rows.length > rowsOnPage"
        :rows-per-page-count="rowsOnPage"
        :virtualize="false"
      >
        <template #items="{ item }">
          <div class="table-cell">{{ item.browser }}</div>
          <div class="table-cell">{{ item.userIp }}</div>
          <div class="table-cell">{{ item.latestActivity }}</div>
          <div class="table-cell latest-sessions__actions-cell">
            <a
              href="#"
              class="latest-sessions__delete"
              @click.prevent="confirmDeleteSession(item)"
            >
              <ui-icon
                size="12"
                :color="colors.red"
                icon="fas fa-trash-alt"
                class="latest-sessions__delete-icon"
              />
              {{ t('Delete') }}
            </a>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import moment from 'moment-timezone';
import { useStore } from '@/store';
import router from '@/routes';
import {
  deleteUserAccessTokenApiRequest,
  deleteAllUserAccessTokensApiRequest,
} from '@/services/api/user-api';
import { useI18n, useColors } from '@/composables';
import { $emitter } from '@/services/emitter';

const props = withDefaults(
  defineProps<{
    dataSessions?: unknown;
    reloadSessions: () => Promise<void>;
    format?: string;
  }>(),
  {
    format: 'DD MMM YYYY HH:mm',
  },
);

const { t } = useI18n();
const colors = useColors();
const store = useStore();

const sessionsLoading = ref(true);
const rows = ref<Record<string, unknown>[]>([]);
const rowsOnPage = 5;

const tableHeaders = computed(() => [
  { text: t('Browser'), value: 'browser', sortable: true },
  { text: 'IP', value: 'userIpSort', sortable: true },
  { text: t('Last Activity'), value: 'latestActivitySortMs', sortable: true },
  { text: '', value: 'actions' },
]);

const confirmDeleteSession = (item: Record<string, unknown>) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: async () => {
        sessionsLoading.value = true;
        try {
          await deleteUserAccessTokenApiRequest(String(item.id));
          await props.reloadSessions();
          $emitter.emit('toast-success', t('The session has been successfully deleted'));
        } finally {
          sessionsLoading.value = false;
        }
      },
    },
  });
};

const applySessionsData = (data: unknown) => {
  const list = Array.isArray(data) ? data : [];
  list.forEach(session => {
    const row = session as Record<string, unknown>;

    row.browser = row.browser != null ? String(row.browser) : '';
    row.userIpSort = row.userIp ? String(row.userIp) : '';

    if (row.userIp) {
      const locationParts: string[] = [];

      if (row.country) {
        locationParts.push(String(row.country));
      }

      if (row.city) {
        locationParts.push(String(row.city));
      }

      const location = locationParts.length ? ` (${locationParts.join(', ')})` : '';
      row.userIp = String(row.userIp) + location;
    }

    if (row.latestActivity) {
      const defaultTimezone = store.user?.defaultTimezone;
      const activityUtc = moment.utc(row.latestActivity as string);
      row.latestActivitySortMs = activityUtc.isValid() ? activityUtc.valueOf() : 0;
      if (activityUtc.isValid()) {
        const activity = activityUtc.clone().tz(defaultTimezone);
        row.latestActivity = activity.format(props.format);
      } else {
        row.latestActivity = '';
      }
    } else {
      row.latestActivitySortMs = 0;
    }
  });
  rows.value = list as Record<string, unknown>[];
};

const deleteAllSessions = async () => {
  sessionsLoading.value = true;
  try {
    await deleteAllUserAccessTokensApiRequest();
    await router.replace('/logout');
  } finally {
    sessionsLoading.value = false;
  }
};

const submit = () => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => deleteAllSessions(),
    },
  });
};

watch(
  () => props.dataSessions,
  dataSessions => {
    if (dataSessions == null) return;
    applySessionsData(dataSessions);
    sessionsLoading.value = false;
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>

.latest-sessions__intro {
  color: var(--color-graphite);
  font-size: 13px;
  line-height: 1.69;
  margin: 0 0 16px;
}

.latest-sessions__table {
  margin-top: 16px;

  :deep(.ui-table__inner) {
    table-layout: fixed;
    width: 100%;
    max-width: 100%;
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-row .table-cell:nth-child(2)),
  :deep(.table-row .table-cell:nth-child(3)) {
    text-align: left;
  }

  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-row .table-cell:nth-child(1)),
  :deep(.table-row .table-cell:nth-child(2)),
  :deep(.table-row .table-cell:nth-child(3)) {
    width: 33% !important;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(4)),
  :deep(.table-row .table-cell:nth-child(4)) {
    width: 60px !important;
    white-space: nowrap;
    text-align: right;
    overflow: visible;
    text-overflow: clip;
  }

  :deep(.table-header .table-cell:nth-child(4):lang(ru)),
  :deep(.table-row .table-cell:nth-child(4):lang(ru)) {
    width: 70px !important;
  }

  :deep(.table-row .table-cell:nth-child(2)) {
    padding-top: 4px;
    padding-bottom: 4px;
    white-space: normal;
  }
}

.latest-sessions__delete {
  color: var(--color-red);
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.latest-sessions__delete-icon {
  margin-right: 4px;
  vertical-align: baseline;
}
</style>
